import { Injectable } from '@angular/core';
import { AvatarSimulationProgressPipe } from '../_pipes/avatar-simulation-progress.pipe';
import { ModuleService } from './module.service';
import { Storage } from '@ionic/storage';
import { ModuleEnum } from '../_utils/unums/module.enum';
import { UserJourneyService } from './user-journey.service';
import { UserSimulationsService } from './user-simulations.service';
import { SimulationService } from './simulation.service';
import { lastValueFrom } from 'rxjs';
import { CompletedModuleStatus } from '../_utils/custom-types';

@Injectable({
  providedIn: 'root'
})
export class UserProgressService {
  private progressSectionStrategy: any;

  constructor(
    private avatarSimulationProgressPipe: AvatarSimulationProgressPipe,
    private storage: Storage,
    private moduleService: ModuleService,
    private userJourneyService: UserJourneyService,
    private userSimulationService: UserSimulationsService,
    private simulationService: SimulationService
  ) {
    this.initProgressSectionStrategy();
  }

  private initProgressSectionStrategy = (): void => {
    this.progressSectionStrategy = {
      avatar: async (module: ModuleEnum, simulationProgressSettings: any) => {
        const user = await this.storage.get('user');
        const avatarSimulations = await lastValueFrom(this.userSimulationService.getUserSimulations(
          { modules: [module], userIds: [user.uid] }));
        const simulationIds = [
          ...new Set(avatarSimulations.map(x => x.simulationId))
        ].filter(x => x);
        const allSimulations = (await Promise.all(simulationIds.map(sim => lastValueFrom(this.simulationService.getSimulationById(sim)))));
        const simulations = [];
        for (const simulationId of simulationIds) {
          const simulation = allSimulations.filter(x => x).find(x => x.id === simulationId);
          if (!simulation) {
            continue;
          }
          simulation.attempts = avatarSimulations.filter(x => x.simulationId === simulationId);
          simulations.push(simulation);
        }
        return {
          key: 'avatar',
          ...(await this.avatarSimulationProgressPipe.transform(simulations, module, simulationProgressSettings))
        };
      },
      peer: async (module: ModuleEnum, simulationProgressSettings: any) => {
        const userJourney = await this.storage.get('user_journey');
        const userModule = userJourney.modules[module];
        const peerSettings = userModule ? simulationProgressSettings.peer.find(x => x.userGrade === userModule.grade) : 0;
        const total = peerSettings?.value || 0;
        const simulations = userModule ? userModule.simulations_peers : 0;
        return {
          key: 'peer',
          total,
          simulations,
          percent: simulations >= total ? 100 : Math.round((simulations / total) * 100)
        };
      },
      bestPractices: async (module: ModuleEnum) => {
        const result = await this.userJourneyService.getUserJourneyPathValue(`/modules/${module}/finished_methodology`);
        return {
          key: 'bestPractices',
          date: result,
          percent: result ? 100 : 0
        };
      },
      introduction: async (module: ModuleEnum) => {
        const result = await this.userJourneyService.getUserJourneyPathValue(`/modules/${module}/finished_introduction`);
        return {
          key: 'introduction',
          date: result,
          percent: result ? 100 : 0
        };
      }
    };
  };

  public progressSectionHandler = async (section: string, module: string): Promise<any> => {
    const progressSetting = await this.userJourneyService.getSimulationProgressSetting();
    return await this.progressSectionStrategy[section](module, progressSetting);
  };

  public getGlobalModuleProgress = async (module: ModuleEnum | string): Promise<number> => {
    const sections = await this.moduleService.getStatusModuleSections(module);
    const activeSections = Object.keys(sections).filter(key => sections[key]);
    const progressSections = await Promise.all(activeSections.map(section => this.progressSectionHandler(section, module)));
    const percentages = await this.moduleService.geModulePercentages(module);
    let total = 0;
    // eslint-disable-next-line guard-for-in
    for (const section in percentages) {
      const sectionPercent = progressSections.find(x => x.key === section)?.percent || 0;
      if (sectionPercent === 0) {
        continue;
      }
      total += (percentages[section] * sectionPercent) / 100;
    }
    return total;
  };

  public async getCompletedModuleStatus(moduleKey: ModuleEnum | string): Promise<CompletedModuleStatus> {
    if (await this.userJourneyService.isDemo()) {
      return;
    }
    const userModule = await this.userJourneyService.getUserJourneyModule(moduleKey);
    if (userModule?.completedStatus === 'done' || userModule?.completedStatus === 'complete') {
      return userModule.completedStatus;
    }
    if (!userModule?.grade) {
      return 'incomplete';
    }
    const percent = await this.getGlobalModuleProgress(moduleKey);
    return percent === 100 ? 'complete' : 'incomplete';
  }
}
